/**
 * When we login on `login-complete.page.tsx` we save the login tracking data to local storage because there's an imediate
 * redirect to the next page, so when the user lands on the next page we can read the data from local storage and track the login.
 *
 * This prevents us from losing the login tracking data.
 */

type PendingLoginTrackingData = {
	isJoin: boolean;
	timestamp: number;
};

const STORAGE_KEY = 'pendingLoginTracking';
const DURATION_MS = 5 * 60 * 1000; // 5 minutes

export function savePendingLoginTracking(isJoin: boolean): void {
	if (typeof window === 'undefined' || !window.localStorage) {
		return;
	}

	try {
		const data: PendingLoginTrackingData = {
			isJoin,
			timestamp: Date.now(),
		};
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch (error) {
		console.warn('Failed to save pending login tracking data:', error);
	}
}

export function readPendingLoginTracking(): PendingLoginTrackingData | null {
	if (typeof window === 'undefined' || !window.localStorage) {
		return null;
	}

	try {
		const stored = window.localStorage.getItem(STORAGE_KEY);
		if (!stored) {
			return null;
		}

		const data = JSON.parse(stored) as PendingLoginTrackingData;

		// Check if data has expired
		if (Date.now() - data.timestamp > DURATION_MS) {
			window.localStorage.removeItem(STORAGE_KEY);
			return null;
		}

		// Clear the data after reading
		window.localStorage.removeItem(STORAGE_KEY);
		return data;
	} catch (error) {
		console.warn('Failed to read pending login tracking data:', error);
		// Clean up potentially corrupted data
		try {
			window.localStorage.removeItem(STORAGE_KEY);
		} catch {}
		return null;
	}
}
